import { useState } from 'react';
import { api, ApiError, type OwnLifeEvent } from '../lib/api.js';

/**
 * Start a thread: a name, a date, and optionally the date your own count runs
 * from. Release 2 of 2.0 (docs/PRD-2.0.md §4).
 *
 * A thread is a life event that is allowed to be ordinary — a race you are
 * training for, a job you came back to, a person — so this is the one place
 * in the app where naming comes first: nobody starts a thread without a word
 * for it, and the thing that was true of catastrophes ("no cursor waiting")
 * is not true of a marathon. The app still never names one for you; the
 * offer path for the big things is untouched and stays wordless.
 *
 * No examples in the placeholder. The rule about not naming somebody's
 * catastrophe for them holds for their race too.
 */
export function StartThread({
  onCreated,
  onCancel,
}: {
  onCreated: (t: OwnLifeEvent) => void;
  onCancel: () => void;
}): JSX.Element {
  const today = new Date().toLocaleDateString('en-CA');
  const [label, setLabel] = useState('');
  const [since, setSince] = useState(today);
  const [countFrom, setCountFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (!label.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const t = await api.createLifeEvent({
        label: label.trim(),
        startedOn: since,
        kind: 'thread',
        countFrom: countFrom || null,
      });
      onCreated(t);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start that.');
      setBusy(false);
    }
  }

  return (
    <form
      className="card stack-sm stack thread-start"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor="thread-name">A thread</label>
        <input
          id="thread-name"
          className="input"
          value={label}
          maxLength={60}
          placeholder="What it is, in a few words"
          onChange={(e) => setLabel(e.target.value)}
          autoFocus
        />
      </div>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <div className="field grow">
          <label htmlFor="thread-since">Since</label>
          <input id="thread-since" className="input" type="date" value={since} max={today} onChange={(e) => setSince(e.target.value)} />
        </div>
        <div className="field grow">
          <label htmlFor="thread-count">Count days from</label>
          <input
            id="thread-count"
            className="input"
            type="date"
            value={countFrom}
            onChange={(e) => setCountFrom(e.target.value)}
          />
        </div>
      </div>
      <p className="faint" style={{ margin: 0 }}>
        The count is yours: "day 25 back at work" counts from the first day back, which may not be the
        day you started the thread. Leave it blank to count from the start.
      </p>
      {error && (
        <p className="faint" role="alert">
          {error}
        </p>
      )}
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <button type="submit" className="btn btn-primary grow" disabled={busy || !label.trim()}>
          {busy ? <span className="spinner" /> : 'Start it'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Not now
        </button>
      </div>
    </form>
  );
}
